import { debounce } from '@/partials/el.Utils.js';

const SELECTOR = {
  CONTAINER: '#el-video-launcher-overlay',
  TEMPLATE: '#el-video-launcher-overlay-template',
  OVERLAY: '.js-video-launcher-overlay',
  IFRAME: '.js-video-launcher-iframe',
  CLOSE_BUTTON: '.js-video-launcher-close',
  TOGGLE_SOUND_BUTTON: '.js-video-launcher-toggle-sound',
  REPLAY_BUTTON: '.js-video-launcher-replay',
  SOUND_STATUS: '.js-sound-status',
  CAPTION: '.js-video-launcher-caption',
};

const VIMEO_API_URL = 'https://player.vimeo.com/api/player.js';

const VIDEO_LINK_SELECTOR = 'a[href*="#video"]';

const BODY_OPEN_CLASS = 'el-video-launcher-overlay-open';

// Below this width the mobile video is used instead of the desktop one.
const MOBILE_BREAKPOINT = 500;

let vimeoApiPromise = null;

let canAudioPlayWithoutInteraction;

const loadVimeoApi = () => {
  if (vimeoApiPromise) {
    return vimeoApiPromise;
  }

  vimeoApiPromise = new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = VIMEO_API_URL;
    script.async = true;
    script.addEventListener('load', resolve, { once: true });
    document.body.appendChild(script);
  });

  return vimeoApiPromise;
};

const getVideoDataFromUrl = (urlString) => {
  const url = new URL(urlString, window.location.origin);
  const hashParams = new URLSearchParams(url.hash.split('?')[1] || '');

  return {
    mobileVideoId: hashParams.get('mobile'),
    desktopVideoId: hashParams.get('desktop'),
    caption: hashParams.get('caption'),
  };
};

elabs.VideoLauncher = class {
  constructor(element) {
    this.view = element;
    this.templateElement = this.view.querySelector(SELECTOR.TEMPLATE);
    this.overlay = null;
    this.iframe = null;
    this.captionElement = null;
    this.soundStatus = null;
    this.player = null;
    this.videoData = null;
    this.currentVideoId = null;

    this.wireEvents();
    this.openFromLocation();
  }

  openFromLocation = () => {
    if (window.location.hash.startsWith('#video')) {
      this.open(getVideoDataFromUrl(window.location.href));
    }
  };

  wireEvents = () => {
    document.addEventListener('click', (event) => {
      const link = event.target.closest(VIDEO_LINK_SELECTOR);
      const isCurrentPage = link?.origin === window.location.origin && link?.pathname === window.location.pathname;
      if (!isCurrentPage) {
        return;
      }

      event.preventDefault();
      this.open(getVideoDataFromUrl(link.href));
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.overlay) {
        this.close();
      }
    });

    window.addEventListener('hashchange', this.openFromLocation);
    window.addEventListener('resize', debounce(this.handleResize, 250));
  };

  open = (videoData) => {
    this.videoData = videoData;
    this.appendTemplate();
    this.launchVideo();
  };

  appendTemplate = () => {
    this.overlay?.remove();

    this.view.appendChild(this.templateElement.content.cloneNode(true));

    this.overlay = this.view.querySelector(SELECTOR.OVERLAY);
    this.iframe = this.view.querySelector(SELECTOR.IFRAME);
    this.captionElement = this.view.querySelector(SELECTOR.CAPTION);
    this.soundStatus = this.view.querySelector(SELECTOR.SOUND_STATUS);
    this.replayButton = this.view.querySelector(SELECTOR.REPLAY_BUTTON);

    this.view.querySelector(SELECTOR.CLOSE_BUTTON).addEventListener('click', this.close);
    this.view.querySelector(SELECTOR.TOGGLE_SOUND_BUTTON).addEventListener('click', this.toggleSound);
    this.replayButton.addEventListener('click', this.replay);
    this.iframe.addEventListener('load', this.initPlayer);

    this.showOverlay();
  };

  showOverlay = () => {
    history.replaceState(null, '', window.location.pathname + window.location.search);

    if (this.videoData.caption) {
      this.captionElement.innerHTML = this.videoData.caption;
    } else {
      this.captionElement.hidden = true;
    }

    this.overlay.classList.add('active');
    document.body.classList.add(BODY_OPEN_CLASS);
  };

  launchVideo = async () => {
    await loadVimeoApi();

    if (canAudioPlayWithoutInteraction === undefined && typeof AudioContext !== 'undefined') {
      canAudioPlayWithoutInteraction = new AudioContext().state === 'running';
    }

    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    const videoId = isMobile ? this.videoData.mobileVideoId : this.videoData.desktopVideoId;

    if (!videoId || videoId === this.currentVideoId) {
      return;
    }

    this.currentVideoId = videoId;

    // "unmute_button=0" hides the player's own unmute pill, the overlay has its own control.
    const embedParams = [
      'autoplay=1',
      'title=0',
      'byline=0',
      'portrait=0',
      'playsinline=1',
      'unmute_button=0',
      'color=5e3a1d',
    ];

    if (!canAudioPlayWithoutInteraction) {
      embedParams.push('muted=1');
    }

    this.iframe.src = `https://player.vimeo.com/video/${videoId}?${embedParams.join('&')}`;
  };

  initPlayer = async () => {
    try {
      this.player?.off('volumechange');
      this.player?.off('ended');
      this.player?.off('play');
      this.player = new window.Vimeo.Player(this.iframe);
      this.replayButton.hidden = true;

      this.player.on('ended', () => {
        this.replayButton.hidden = false;
      });
      this.player.on('play', () => {
        this.replayButton.hidden = true;
      });
      this.player.on('volumechange', async () => {
        this.updateSoundStatus(await this.player.getMuted());
      });

      await this.player.ready();
      this.updateSoundStatus(await this.player.getMuted());
    } catch {
      this.player = null;
    }
  };

  toggleSound = async () => {
    if (!this.player) {
      return;
    }

    const muted = await this.player.getMuted();
    await this.player.setVolume(muted ? 1 : 0);
  };

  replay = async () => {
    if (!this.player) {
      return;
    }

    this.replayButton.hidden = true;

    try {
      await this.player.setCurrentTime(0);
      await this.player.play();
    } catch {
      this.replayButton.hidden = false;
    }
  };

  updateSoundStatus = (muted) => {
    const { soundOn, soundOff } = this.soundStatus.dataset;
    this.soundStatus.textContent = muted ? soundOn : soundOff;
  };

  handleResize = () => {
    if (!this.overlay) {
      return;
    }

    this.launchVideo();
  };

  close = () => {
    if (!this.overlay) {
      return;
    }

    this.player?.off('volumechange');
    this.player?.off('ended');
    this.player?.off('play');
    this.player = null;

    this.overlay.classList.remove('active');
    this.overlay.remove();
    this.overlay = null;
    this.currentVideoId = null;

    document.body.classList.remove(BODY_OPEN_CLASS);
  };
};

const container = document.querySelector(SELECTOR.CONTAINER);

if (container) {
  void new elabs.VideoLauncher(container);
}
